import { Card, Text } from '@components/ui';
import type { JobData } from '@features/jobs/jobData';
import Box from '@mui/material/Box';
import Link from '@mui/material/Link';
import Switch from '@mui/material/Switch';
import { useTranslation } from 'react-i18next';

import { JobFacts } from './job-field';

const sectionTitleSx = {
  mb: 1.5,
};

interface JobInfoPanelProps {
  job: JobData;
  showOnMyWay: boolean;
  onMyWay: boolean;
  onMyWayChange: (checked: boolean) => void;
  linkUrl: string | null;
}

const JobInfoPanel = ({
  job,
  showOnMyWay,
  onMyWay,
  onMyWayChange,
  linkUrl,
}: JobInfoPanelProps): JSX.Element => {
  const { t } = useTranslation();

  return (
    <Card sx={{ mb: 2 }}>
      <Text as="h2" variant="subtitle" sx={sectionTitleSx}>
        {t('jobDetail.info')}
      </Text>
      <JobFacts
        items={[
          {
            label: t('jobDetail.description'),
            value: job.description,
            wide: true,
          },
          { label: t('jobDetail.year'), value: job.year },
          { label: t('jobDetail.make'), value: job.make },
          { label: t('jobDetail.model'), value: job.model },
          { label: t('jobDetail.vin'), value: job.vinOrSerial },
          { label: t('jobDetail.askingPrice'), value: job.askingPrice },
        ]}
      />
      {linkUrl ? (
        <Box
          sx={{
            mt: 2,
            borderRadius: 1,
            bgcolor: 'action.hover',
            px: 1.5,
            py: 1,
          }}
        >
          <Text
            as="span"
            variant="overline"
            tone="muted"
            sx={{ display: 'block' }}
          >
            {t('jobDetail.linkToList')}
          </Text>
          <Link
            href={linkUrl}
            target="_blank"
            rel="noopener noreferrer"
            underline="hover"
            sx={{
              display: 'inline-block',
              mt: 0.25,
              maxWidth: '100%',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              fontSize: 14,
              fontWeight: 600,
              color: 'primary.main',
              cursor: 'pointer',
            }}
          >
            {linkUrl}
          </Link>
        </Box>
      ) : null}
      {showOnMyWay && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            py: '14px',
          }}
        >
          <Text as="span" variant="overline" tone="muted">
            {t('jobDetail.onMyWay')}
          </Text>
          <Switch
            checked={onMyWay}
            onChange={(event) => onMyWayChange(event.target.checked)}
            sx={{
              '& .MuiSwitch-switchBase.Mui-checked': {
                color: 'rgb(var(--primary))',
              },
              '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                backgroundColor: 'rgb(var(--primary))',
              },
            }}
          />
        </Box>
      )}
    </Card>
  );
};

export default JobInfoPanel;
